import { inject, Injectable } from '@angular/core';

import { Bank, Iban, SortCode } from '@formunauts/banking/domain';
import { CountryBankData } from './bank-data/country-bank-data';
import { BANKS_COUNTRY_REGISTRY_TOKEN } from './banking-data-acess.tokens';
import { IbanService } from './iban.service';

export enum FindResult {
  Success,
  /**
   * There is no data available for this country
   */
  NoCountryData,
  BankNotFound,
  Undefined,
}

interface FindBankResult {
  result: FindResult;
  countryBankData?: CountryBankData;
  bank?: Bank.LegacyBank;
}

@Injectable()
export class BankService {
  private readonly _countryBankData = inject(BANKS_COUNTRY_REGISTRY_TOKEN);

  private readonly ibanService = inject(IbanService);

  /**
   * @param iban The iban for which a bank should be found
   *
   * @returns
   * An object indicating the result of the find operation.
   */
  public findByIban(iban: Iban.LegacyIban): FindBankResult {
    const { countryCode } = iban;
    const result: FindBankResult = {
      result: FindResult.Undefined,
    };

    // First, look for the country bank
    const countryBankData = this._countryBankData.get(countryCode);
    if (!countryBankData) {
      return {
        ...result,
        result: FindResult.NoCountryData,
      };
    }

    // Then look for the bank
    const bankInfo = this.ibanService.getBankInfo(iban);
    result.countryBankData = countryBankData;
    const bank = countryBankData.findBank(bankInfo!.bankId, bankInfo!.branchId);
    if (!bank) {
      return {
        ...result,
        result: FindResult.BankNotFound,
      };
    }

    // Finally, return everything we found
    result.bank = bank;
    return {
      ...result,
      result: FindResult.Success,
    };
  }

  /**
   * @param sortCode
   * @returns The Bank of the given Sort Code
   */
  public findBySortCode(sortCode: SortCode.LegacySortCode): FindBankResult {
    const result: FindBankResult = {
      result: FindResult.Undefined,
    };

    const countryBankData = this._countryBankData.get('GB');

    // look for the bank
    const bank = countryBankData?.findBank(undefined!, sortCode.format(''));

    // no bank found
    if (!bank) {
      return {
        ...result,
        result: FindResult.BankNotFound,
      };
    }

    // bank found
    result.bank = bank;
    return {
      ...result,
      result: FindResult.Success,
    };
  }
}
